// src/frontend/js/modern/core/persistence-writer.js
import { stateCache } from './state-cache.js';
import { resilientStorage } from './storage/resilient-storage.js';
import { STORAGE_KEYS } from '../shared/constants.js';

const MODULE_KEY_MAP = {
	wishlist: STORAGE_KEYS.WISHLIST,
	waitlist: STORAGE_KEYS.WAITLIST,
	multiList: STORAGE_KEYS.MULTI_LIST,
};

const _lastHashes = new Map();

function quickHash(obj) {
	const str = JSON.stringify(obj);
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = ((hash << 5) - hash) + char;
		hash |= 0;
	}
	return hash;
}

async function writeModule(module) {
	const key = MODULE_KEY_MAP[module];
	if (!key) return;

	const data = module === 'multiList'
		? { lists: stateCache.getLists(), count: stateCache.getCount(module) }
		: { items: stateCache.getItems(module), count: stateCache.getCount(module) };

	const hash = quickHash(data);
	if (_lastHashes.get(module) === hash) return;
	_lastHashes.set(module, hash);

	const formatted = { ...data, lastUpdated: new Date().toISOString() };

	try {
		localStorage.setItem(key, JSON.stringify(formatted));
	} catch (e) {
		if (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014) {
			for (let i = localStorage.length - 1; i >= 0; i--) {
				const k = localStorage.key(i);
				if (k && k.startsWith('wlfmc_') && k !== key) {
					localStorage.removeItem(k);
					try { localStorage.setItem(key, JSON.stringify(formatted)); break; } catch (err) {}
				}
			}
		}
	}

	try {
		await resilientStorage.set(key, { __wlfmc_ts: Date.now(), data: formatted });
	} catch (e) { /* IDB failed, localStorage is enough */ }
}

export function initPersistenceWriter() {
	document.addEventListener('wlfmc:cache-flush', (event) => {
		const { modules } = event.detail || {};
		if (!modules || modules.length === 0) return;
		for (const module of modules) writeModule(module);
	});
	window.addEventListener('beforeunload', () => {
		for (const module of Object.keys(MODULE_KEY_MAP)) writeModule(module);
	});
	console.log('[PersistenceWriter] Initialized (Direct Storage Mode)');
}

export async function recoverFromPersistence() {
	for (const [module, key] of Object.entries(MODULE_KEY_MAP)) {
		try {
			let storeData = null;
			const raw = localStorage.getItem(key);
			if (raw) { try { storeData = JSON.parse(raw); } catch {} }

			if (!storeData) {
				const envelope = await resilientStorage.get(key);
				if (envelope && envelope.data) storeData = envelope.data;
			}

			if (storeData) {
				if (module === 'multiList') {
					stateCache.setModuleData(module, storeData.lists || [], storeData.count || 0);
				} else {
					stateCache.setModuleData(module, storeData.items || [], storeData.count || 0);
				}
			}
		} catch (error) {
			console.warn(`[PersistenceWriter] Recovery failed for ${module}:`, error);
		}
	}
}

export default { initPersistenceWriter, recoverFromPersistence };
